import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { SFContentContainer, SFLoadingPage } from "@sf-core";
import { selectCoursesBySkillPathLevelId } from "../../redux/reducers/courseReducer";
import {
  getDomain,
  getJobCoursesWithSkills,
} from "../../redux/reducers/domainReducer";
import {
  selectDomainSkillPathLevels,
  selectJobSkillPathLevels,
  getSkillPathLevels,
} from "../../redux/reducers/skillPathLevelsReducer";
import { userConfigurationActions } from "../../redux/reducers/userConfigurationReducer";
import DomainPage from "../../components/domainPage";
import { useTranslation } from "react-i18next";
import { profileActions } from "../../redux/reducers/profileReducer";
import { getProfileUrl } from "../../constants/urls";
import { PROFILE_STEPS } from "../../constants/profileSteps";
import { selectDomainsByType } from "../../redux/reducers/domainReducer";
import { DOMAIN_TYPES } from "../../constants/domainTypes";
import { getJobsUsingSkill, getUserDomainRecommendedSkillPaths } from "../../services/domainService";
import { getHomeUrl } from "../../constants/urls";
import {
  removeSkillGoal,
  getSkillDetails,
} from "../../services/profileService";
import { removeDomainsByType } from "../../redux/reducers/domainReducer";

const SkillScreen = () => {
  let { id } = useParams();
  id = id.toLowerCase();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [jobsUsingSkill, setJobsUsingSkill] = useState([]);
  const [microSkills, setMicroSkills] = useState([]);
  const [subTitle, setSubTitle] = useState(null);
  const userSkillGoal = useSelector((state) =>
    selectDomainsByType(state, DOMAIN_TYPES.USER_SKILL_GOAL)
  );
  let isSkillGoal = userSkillGoal.some((skill) => skill.id === id);
  const [recommendedSkillPaths, setRecommendedSkillPaths] = useState([]);
  const [currentState, setCurrentState] = useState({
    skillPathLevelId: null,
    recommendedSkillPathLevelId: null,
    skillPathId: null,
    selectedSkills: [],
  });
  const [contentOptions, setContentOptions] = useState([]);
  const skill = useSelector((state) => state.domains.find((x) => x.id === id));

  const existingSkillGoals = useSelector((state) => state.profile.skillGoals);

  const skillPathLevels = useSelector((state) =>
    selectDomainSkillPathLevels(state, id)
  );

  const recommendedSkillPathLevels = useSelector((state) =>
    selectJobSkillPathLevels(state).filter((level) =>
      recommendedSkillPaths
        .map((path) => path.levelId)
        .includes(level.id)
    )
  );

  const courses = useSelector((state) =>
    selectCoursesBySkillPathLevelId(
      state,
      id,
      currentState.skillPathLevelId,
      currentState.selectedSkills
    )
  );

  const lastAccessed = useSelector((state) =>
    state.userConfiguration.lastAccessed.find((x) => x.domainId === id)
  );

  const lxpEnabledLearnerJobAndSkillVerification = useSelector(
    (state) =>
      state.session.tenantSettings.lxpEnabledLearnerJobAndSkillVerification
  );

  useEffect(() => {
    getUserDomainRecommendedSkillPaths(id).then((response) => {
      setRecommendedSkillPaths(response.data);
    });
  }, [id]);
  
  useEffect(() => {
    dispatch(getDomain(id));
    dispatch(getSkillPathLevels());
    dispatch(getJobCoursesWithSkills(id));
    getJobsUsingSkill(skill?.internalReference).then((response) => {
      setJobsUsingSkill(response.data);
    });
    getSkillDetails(skill?.internalReference).then((response) => {
      const skillMicroSkills = response.data.microSkills.map((mircoSkill) => ({
        id: mircoSkill.microSkillCode,
        name: mircoSkill.microSkillName,
      }));
      setMicroSkills(skillMicroSkills);
    });
  }, [id]);

  useEffect(() => {
    if (skillPathLevels.length !== 0) {
      setCurrentState((prv) => ({
        ...prv,
        skillPathLevelId:
          lastAccessed?.skillPathLevelId ?? skillPathLevels[0].id,
      }));

      if (lastAccessed?.skillPathLevelId) {
        dispatch(userConfigurationActions.removeLastAccessed({ domainId: id }));
      }
      setContentOptions((prevContentOptions) =>
        prevContentOptions.some((item) => item.id === 1)
          ? prevContentOptions
          : [{ id: 1, label: t("jobScreen.tabCourses") }, ...prevContentOptions]
      );
    }
  }, [skillPathLevels.length]);

  const skillMenuTabs = useMemo(() => {
    const tabs = [
      { label: t("skillGoalScreen.tabMicroSkills") },
      ...(jobsUsingSkill.length > 0
        ? [{ label: t("skillGoalScreen.tabJobsUsingSkill") }]
        : []),
    ];
    return tabs;
  }, [jobsUsingSkill.length]);

  // Set SubTitle for Avatar Menu
  useEffect(() => {
    if (isSkillGoal) setSubTitle(t("skillGoalScreen.subTitle"));
  }, [isSkillGoal]);

  // Get DropDown Actions for Job Role and Job of Interest
  const getDropDownActions = (navigate, t) => {
    if (isSkillGoal && lxpEnabledLearnerJobAndSkillVerification) {
      return {
        actions: [
          {
            label: t("skillGoalScreen.dropDownManage"),
            onClick: () => {
              dispatch(
                profileActions.setState({
                  activeStep: PROFILE_STEPS.SKILL_GOAL,
                })
              );
              // Navigate to Profile Setup Skill Goal page
              navigate(getProfileUrl());
            },
          },
          {
            label: t("skillGoalScreen.dropDownRemove"),
            onClick: () => {
              removeSkillGoal(skill?.internalReference).then(() => {
                dispatch(removeDomainsByType(DOMAIN_TYPES.USER_SKILL_GOAL));
                // Remove Skill Goal also from redux state
                const skillGoals = existingSkillGoals.filter(
                  (x) => x.id !== skill?.internalReference
                );
                dispatch(profileActions.setSkillGoals(skillGoals));
                navigate(getHomeUrl());
              });
            },
          },
        ],
      };
    }
    return null;
  };

  if (!skill) {
    return (
      <SFContentContainer>
        <SFLoadingPage />
      </SFContentContainer>
    );
  }

  return (
    <DomainPage
      backgroundImage={skill.backgroundImage}
      domain={skill}
      menuTabs={skillMenuTabs}
      allSkills={microSkills}
      currentState={currentState}
      setCurrentState={setCurrentState}
      skillPathLevels={skillPathLevels}
      contentOptions={contentOptions}
      setContentOptions={setContentOptions}
      recommendedSkillPathLevels={recommendedSkillPathLevels}
      courses={courses}
      recommendedSkillPaths={recommendedSkillPaths}
      relatedDomains={jobsUsingSkill}
      showAvatar={isSkillGoal} //True If this is user's skill goal page
      subTitle={subTitle}
      getDropDownActions={getDropDownActions}
      isSkillGoalPage={true}
    />
  );
};

export default SkillScreen;
